import axios from 'axios'
import { auth } from '@/firebase/config'
import { getLocalSession, refreshLocalSession } from './localAuthSession'

/**
 * Resolve API base URL.
 * Production Netlify deploys the Node API at same-origin /api.
 * Ignore mistaken VITE_API_URL values that point at the Python face service.
 */
export function resolveApiBaseUrl() {
  const raw = (import.meta.env.VITE_API_URL || '').trim().replace(/\/$/, '')
  const looksLikeFaceService =
    /attendance-ms-production/i.test(raw) ||
    /facial-recognition/i.test(raw) ||
    /VITE_FACE/i.test(raw)

  if (raw && !looksLikeFaceService) {
    return raw
  }

  if (looksLikeFaceService) {
    console.warn(
      '[api] VITE_API_URL points at the face service — using same-origin /api (Neon Node API) instead:',
      raw
    )
  }

  if (import.meta.env.PROD && typeof window !== 'undefined') {
    return `${window.location.origin}/api`
  }

  // Local Vite → local Node API
  return raw || 'http://localhost:8080'
}

let cachedBase = null
export function getApiBaseUrl() {
  if (!cachedBase) cachedBase = resolveApiBaseUrl()
  return cachedBase
}

export const apiClient = axios.create({
  timeout: 60000,
})

apiClient.interceptors.request.use(async (config) => {
  config.baseURL = getApiBaseUrl()
  let localSession = getLocalSession()
  if (localSession?.accessToken && Date.parse(localSession.accessExpiresAt || 0) < Date.now() + 60_000) {
    try {
      localSession = await refreshLocalSession(config.baseURL)
    } catch (_) {
      localSession = null
    }
  }
  if (localSession?.accessToken) {
    config.headers.Authorization = `Bearer ${localSession.accessToken}`
  } else if (auth?.currentUser) {
    const token = await auth.currentUser.getIdToken()
    config.headers.Authorization = `Bearer ${token}`
  }
  if (config.headers['X-Skip-Auth']) {
    delete config.headers.Authorization
    delete config.headers['X-Skip-Auth']
  }
  return config
})

export async function apiGet(path, config) {
  const res = await apiClient.get(path, config)
  return res.data
}

export async function apiPost(path, body, config) {
  const res = await apiClient.post(path, body, config)
  return res.data
}

export async function apiPut(path, body, config) {
  const res = await apiClient.put(path, body, config)
  return res.data
}

export async function apiPatch(path, body, config) {
  const res = await apiClient.patch(path, body, config)
  return res.data
}

export async function apiDelete(path, config) {
  const res = await apiClient.delete(path, config)
  return res.data
}
